import React, { ReactNode, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { space } from 'styled-system';
import { ClassNameMap } from '@material-ui/styles';
import MuiDrawer from '@material-ui/core/Drawer';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useTranslation from 'hooks/use-translation';
import useUI from 'hooks/use-ui';

import { MobileFilterIcon } from 'src/assets/mobile-filter-icon';
import ExitIcon from 'assets/exit-icon-2';
import { Clickable } from 'shared/components';
import { Button, CollapsibleSection } from 'components/core';
import { heading3, heading5 } from 'components/core/typography';
import IframeHeightOverride from 'components/core/iframe-height-override';

type DrawerProps = {
  open: boolean;
  isEmbedded?: boolean;
  onClose: () => void;
  applyFilters: () => void;
  clearFilters: () => void;
  filterCount: number;
  classes?: ClassNameMap;
  children: ReactNode;
  anchor?: string;
};

export function Drawer({
  open,
  anchor = `top`,
  onClose,
  isEmbedded,
  applyFilters,
  clearFilters,
  filterCount,
  classes,
  children,
}: DrawerProps): JSX.Element {
  const { t } = useTranslation();
  const flags = useFlags();
  const UI = useUI();
  const disableAnimation = flags['ecomm.menu-filters-drawer-animation.kill-switch'] ?? false;
  // Embedded: size to content and report that height to the host via iframe:resize:override, like the
  // mobile nav drawer (ECOMM-14193). vh/dvh inside an iframe resolve to the iframe height, which is as
  // tall as the whole product list, leaving thousands of px of blank drawer.
  // The drawer is fixed at iframe y=0 and the iframe shrinks to the drawer height on open. Bring the host
  // to the iframe top first so the drawer is in view instead of the host scroll being clamped past it.
  useEffect(() => {
    if (open && isEmbedded) {
      UI.embeddedEventDispatcher('iframe:scrollToTop', { force: true });
    }
  }, [open, isEmbedded, UI]);

  const ContentWrapper = isEmbedded ? EmbeddedContentWrapper : DefaultContentWrapper;

  return (
    <StyledDrawer
      classes={classes}
      anchor={anchor}
      open={open}
      transitionDuration={disableAnimation ? 0 : 200}
      aria-labelledby={t('menu.filtersDrawer', 'filters Drawer')}
      aria-describedby={t('menu.filtersDrawerDescription', 'menu filter controls')}
      onClose={onClose}
      ModalProps={{ style: open ? undefined : { pointerEvents: 'none' } }}
      _isEmbedded={isEmbedded}
    >
      <ContentWrapper>
        <HeaderWrapper>
          <HeaderContent>
            <MobileFilterIcon color='var(--dcm-color-text-secondary, #a0a7ab)' />
            <Heading>{t('mobileFilters.filters', 'Filters')}</Heading>
            <CloseButton onClick={onClose}>
              <ExitIcon fill='var(--dcm-color-text-secondary, #a0a7ab)' height='22px' />
            </CloseButton>
          </HeaderContent>

          <CollapsibleSectionStyled>
            <Button width='100%' onClick={applyFilters}>
              {t('mobileFilters.applyFilters', 'Apply Filters')}{' '}
              {filterCount > 0 && <FilterCount>({filterCount})</FilterCount>}
            </Button>
            <ClearAllButton onClick={clearFilters}>{t('mobileFilters.clearFilters', 'Clear all')}</ClearAllButton>
          </CollapsibleSectionStyled>
        </HeaderWrapper>

        <ScrollableContainer>{children}</ScrollableContainer>
      </ContentWrapper>
    </StyledDrawer>
  );
}

const StyledDrawer = styled(({ _isEmbedded, open, ...props }) => <MuiDrawer open={open} {...props} />)`
  ${space}
  font-family: var(--dct--customized-fonts-secondary);

  .MuiBackdrop-root {
    background-color: ${({ theme, _isEmbedded }) => (_isEmbedded ? theme.colors.whiteOverlay : `transparent`)};
  }
  .MuiDrawer-paperAnchorTop {
    /* 100vh includes the browser chrome (address/nav bars) on mobile Android Chrome/Edge,
       which don't shrink the viewport the way iOS Safari does. That leaves part of the
       drawer rendered below the visible area, and overflow: hidden here means it can never
       be scrolled into view (ECOMM-12860). 100dvh tracks the actual visible viewport on
       browsers that support it; the 100vh line above remains as a fallback. */
    ${({ _isEmbedded }) =>
      _isEmbedded
        ? css`
            /* Content-sized (ECOMM-14193). Do not reintroduce vh/dvh/100% here: the measured height would
               equal the iframe height and the override could never shrink it (ECOMM-8834 ratchet).
               MUI sets max-height: 100% on the paper, which would clip to the current iframe height. */
            height: auto;
            max-height: none;
          `
        : css`
            height: 100vh;
            height: 100dvh;
          `}
    overflow: hidden;
    background-color: var(--dcm-color-surface, ${({ theme }) => theme.colors.white});
  }
`;

const CollapsibleSectionStyled = styled(CollapsibleSection)`
  width: 100%;
`;

const ScrollableContainer = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const contentWrapperStyles = css`
  padding: 16px 24px 0;
  display: flex;
  flex-direction: column;
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
`;

const DefaultContentWrapper = styled.div`
  ${contentWrapperStyles}
  height: 100vh;
  height: 100dvh;
`;

const EmbeddedContentWrapper = styled(IframeHeightOverride)`
  ${contentWrapperStyles}
`;

const Heading = styled.h3`
  ${heading3}
  margin: 0 auto 0 11px;
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.grey[25]});
`;

const CloseButton = styled(Clickable)`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  margin-left: 14px;
  height: 42px;
  width: 42px;
`;

const HeaderContent = styled.div`
  display: flex;
  height: 32px;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  min-height: 45px;
`;

const FilterCount = styled.span`
  font-variant-ligatures: none;
`;

const ClearAllButton = styled(Clickable)`
  ${heading5}
  color: var(--dct--customized-colors-buttonsLinks);
  display: inline-block;
  font-weight: bold;
  margin: 24px auto 0 auto;
  width: 5rem;
`;
